// OpenRouter credit balance chip. Lives in the TabBar beside the
// theme icon.
//
// Clicking the chip opens a popout with useful context: remaining,
// total credits, total usage, last-refreshed timestamp, and a link
// to the OpenRouter dashboard. Captain 2026-04-23.
//
// Polls every 60s as a heartbeat and listens for a custom
// "wt:or-balance-refresh" event so any call path that consumes
// credits (council chat, model chat, avatar gen) can trigger an
// immediate refresh after it lands. Dispatch with:
//   window.dispatchEvent(new CustomEvent("wt:or-balance-refresh"));

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Lock, Unlock } from "lucide-react";
import { openRouterCredits } from "../api/client";
import {
  isCreditsLocked,
  onCreditsLockChange,
  setCreditsLocked,
} from "./CreditLock";
import { setKeyConfigured } from "./KeyStatus";

const POLL_MS = 60_000;
const REFRESH_EVENT = "wt:or-balance-refresh";

export function triggerBalanceRefresh() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
}

interface Props {
  orgId: number | null;
}

interface BalanceData {
  total_credits: number;
  total_usage: number;
  remaining: number;
  fetchedAt: number;
}

function formatMoney(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

function formatAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 5_000) return "just now";
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

type FetchStatus = "loading" | "ok" | "not_configured" | "error";

export function OpenRouterBalance({ orgId }: Props) {
  const [data, setData] = useState<BalanceData | null>(null);
  const [status, setStatus] = useState<FetchStatus>("loading");
  const [open, setOpen] = useState(false);
  const [locked, setLocked] = useState(() => isCreditsLocked(orgId));
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocked(isCreditsLocked(orgId));
    return onCreditsLockChange(() => setLocked(isCreditsLocked(orgId)));
  }, [orgId]);

  function toggleLock() {
    if (orgId == null) return;
    setCreditsLocked(orgId, !locked);
  }

  useEffect(() => {
    if (orgId == null) return;
    let cancelled = false;
    async function load() {
      if (orgId == null) return;
      try {
        const resp = await openRouterCredits(orgId);
        if (cancelled) return;
        setKeyConfigured(orgId, resp.configured);
        if (!resp.configured) {
          setData(null);
          setStatus("not_configured");
          return;
        }
        setData({
          total_credits: resp.total_credits,
          total_usage: resp.total_usage,
          remaining: resp.remaining,
          fetchedAt: Date.now(),
        });
        setStatus("ok");
      } catch {
        if (!cancelled) setStatus("error");
      }
    }
    void load();
    const intervalId = window.setInterval(load, POLL_MS);
    const refresh = () => void load();
    window.addEventListener(REFRESH_EVENT, refresh);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener(REFRESH_EVENT, refresh);
    };
  }, [orgId]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (orgId == null) return null;

  const label =
    status === "ok" && data
      ? formatMoney(data.remaining)
      : status === "loading"
        ? "…"
        : "—";

  const chipTone = locked
    ? "animate-pulse text-warning hover:text-warning"
    : status === "not_configured"
      ? "animate-pulse text-warning hover:text-warning"
      : status === "error"
        ? "text-danger hover:text-danger"
        : "";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative cursor-pointer transition-colors ${
          open ? "text-primary" : "text-muted hover:text-primary"
        } ${chipTone}`}
        title={
          locked
            ? "Credits locked. Unlock from the popout."
            : status === "not_configured"
              ? "No OpenRouter key."
              : status === "error"
                ? "Balance unavailable."
                : "OpenRouter balance"
        }
        data-testid="or-balance-toggle"
      >
        {status === "not_configured" ? (
          <AlertTriangle size={14} />
        ) : (
          label
        )}
        {open && (
          <span
            aria-hidden
            className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
          />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-3 w-60 rounded-md border border-edge bg-surface-raised p-3 shadow-[var(--wt-shadow)]"
          data-testid="or-balance-popout"
          role="menu"
        >
          {status === "not_configured" && (
            <p className="text-xs">
              <Link
                to="?tab=settings#provider-keys"
                className="cursor-pointer text-warning underline decoration-dotted underline-offset-2 hover:text-primary"
              >
                Add an OpenRouter key
              </Link>
            </p>
          )}
          {status === "error" && (
            <p className="text-xs">
              <Link
                to="?tab=settings#provider-keys"
                className="cursor-pointer text-danger underline decoration-dotted underline-offset-2 hover:text-primary"
              >
                Check your OpenRouter key
              </Link>
            </p>
          )}
          {status === "ok" && data && (
            <>
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-xs font-medium text-muted">Remaining</span>
                <span className="font-code text-sm text-primary">
                  {formatMoney(data.remaining)}
                </span>
              </div>
              <div className="flex items-baseline justify-between border-t border-edge pt-2 text-xs">
                <span className="text-muted">Total credits</span>
                <span className="font-code text-primary">
                  {formatMoney(data.total_credits)}
                </span>
              </div>
              <div className="mt-1 flex items-baseline justify-between text-xs">
                <span className="text-muted">Used</span>
                <span className="font-code text-primary">
                  {formatMoney(data.total_usage)}
                </span>
              </div>
              <div className="mt-3 flex items-baseline justify-between border-t border-edge pt-2 text-[0.625rem] text-faint">
                <span>Updated {formatAgo(data.fetchedAt)}</span>
                <span>refreshes on use</span>
              </div>
            </>
          )}
          {status === "loading" && !data && (
            <p className="text-xs text-muted">Fetching balance…</p>
          )}
          <button
            type="button"
            onClick={toggleLock}
            className={`mt-3 flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-xs transition-colors ${
              locked
                ? "border-warning/60 bg-warning/10 text-warning hover:bg-warning/15"
                : "border-edge text-muted hover:border-accent hover:text-primary"
            }`}
            data-testid="or-balance-lock-toggle"
            aria-pressed={locked}
          >
            <span className="flex items-center gap-2">
              {locked ? <Lock size={12} /> : <Unlock size={12} />}
              <span>{locked ? "Credits locked" : "Lock credits"}</span>
            </span>
            <span className="text-[0.625rem] text-faint">
              {locked ? "click to unlock" : "halts spend paths"}
            </span>
          </button>
          {locked && (
            <p className="mt-2 text-[0.625rem] text-faint">
              Cap tripped? Raise it in{" "}
              <Link
                to="?tab=settings#limits"
                className="cursor-pointer underline decoration-dotted underline-offset-2 hover:text-primary"
              >
                Settings → Limits
              </Link>
              .
            </p>
          )}
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-edge pt-2 text-xs">
            <a
              href="https://openrouter.ai/credits"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-muted hover:text-primary"
            >
              OpenRouter →
            </a>
            <button
              type="button"
              onClick={() => triggerBalanceRefresh()}
              className="cursor-pointer text-muted transition-colors hover:text-primary"
              data-testid="or-balance-refresh"
            >
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
